import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { CalendarClock, PhoneCall, CheckCircle2, XCircle, CalendarPlus, AlertTriangle, Search } from 'lucide-react';
/** Returns ms until the start of the next minute. Used to schedule a tick that
 *  keeps the "now" reference fresh so urgency colors stay correct in real time. */
const msUntilNextMinute = () => {
  const now = new Date();
  return 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
};
type Urgency = 'overdue' | 'soon';
const REVIEW_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;
interface ReviewRow {
  encounterId: string;
  patientId: string;
  patientName: string;
  bmcFileNumber: string;
  phone: string;
  reviewDate: Date;
  reviewDateISO: string;
  status: 'Pending' | 'Contacted' | 'Cancelled' | 'Rescheduled';
  contactedAt?: string;
  urgency: Urgency;
}
const urgencyStyles: Record<Urgency, {
  row: string;
  pill: string;
  label: string;
}> = {
  overdue: {
    row: 'bg-red-50 border-l-4 border-red-500 hover:bg-red-100',
    pill: 'bg-red-600 text-white',
    label: 'Review Overdue'
  },
  soon: {
    row: 'bg-amber-50 border-l-4 border-amber-500 hover:bg-amber-100',
    pill: 'bg-amber-500 text-white',
    label: 'Review Due Soon'
  }
};
const classifyUrgency = (reviewDate: Date, now: Date): Urgency | null => {
  // - overdue: the review date passed 2 or more days ago
  // - soon: the review date is within 2 days of now (next 2 days, today,
  //   or missed by less than 2 days)
  // - anything more than 2 days in the future is not shown
  const diffMs = reviewDate.getTime() - now.getTime();
  if (diffMs <= -REVIEW_WINDOW_MS) return 'overdue';
  if (diffMs <= REVIEW_WINDOW_MS) return 'soon';
  return null;
};
export const PatientReviewDueList: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [now, setNow] = useState<Date>(() => new Date());
  // For the inline reschedule mini-form. Maps encounterId → new YYYY-MM-DDTHH:mm
  const [rescheduling, setRescheduling] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  // Re-tick at the top of each minute so urgency classifications stay fresh.
  useEffect(() => {
    let timeoutId: number;
    const schedule = () => {
      timeoutId = window.setTimeout(() => {
        setNow(new Date());
        schedule();
      }, msUntilNextMinute());
    };
    schedule();
    return () => window.clearTimeout(timeoutId);
  }, []);
  const rows: ReviewRow[] = useMemo(() => {
    const out: ReviewRow[] = [];
    for (const enc of encounters) {
      if (!enc.reviewDate) continue;
      if (enc.reviewStatus === 'Cancelled') continue;
      const reviewDate = new Date(enc.reviewDate);
      if (Number.isNaN(reviewDate.getTime())) continue;
      const urgency = classifyUrgency(reviewDate, now);
      if (!urgency) continue;
      const patient = patients.find(p => p.id === enc.patientId);
      if (!patient) continue;
      out.push({
        encounterId: enc.id,
        patientId: patient.id,
        patientName: `${patient.firstName} ${patient.lastName}`,
        bmcFileNumber: patient.bmcFileNumber,
        phone: patient.contact || '—',
        reviewDate,
        reviewDateISO: enc.reviewDate,
        status: enc.reviewStatus || 'Pending',
        contactedAt: enc.reviewContactedAt,
        urgency
      });
    }
    // Most urgent first: overdue → today → soon. Within a bucket, earliest review date first.
    const urgencyRank: Record<Urgency, number> = {
      overdue: 0,
      soon: 1
    };
    return out.sort((a, b) => {
      const u = urgencyRank[a.urgency] - urgencyRank[b.urgency];
      if (u !== 0) return u;
      return a.reviewDate.getTime() - b.reviewDate.getTime();
    });
  }, [encounters, patients, now]);
  const counts = useMemo(() => ({
    overdue: rows.filter(r => r.urgency === 'overdue').length,
    soon: rows.filter(r => r.urgency === 'soon').length
  }), [rows]);
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.patientName.toLowerCase().includes(q) || r.bmcFileNumber.toLowerCase().includes(q) || r.phone.toLowerCase().includes(q));
  }, [rows, search]);
  const handleMarkContacted = (encounterId: string) => {
    updateEncounter(encounterId, {
      reviewStatus: 'Contacted',
      reviewContactedAt: new Date().toISOString(),
      reviewContactedBy: currentUser?.id
    });
  };
  const handleCancelReview = (encounterId: string, patientName: string) => {
    if (!window.confirm(`Cancel the scheduled review for ${patientName}? This removes them from the due list.`)) return;
    updateEncounter(encounterId, {
      reviewStatus: 'Cancelled'
    });
  };
  const handleOpenReschedule = (row: ReviewRow) => {
    // Pre-fill with the existing review date so the receptionist only edits if needed.
    const d = row.reviewDate;
    const pad = (n: number) => String(n).padStart(2, '0');
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setRescheduling(r => ({
      ...r,
      [row.encounterId]: local
    }));
  };
  const handleConfirmReschedule = (encounterId: string) => {
    const next = rescheduling[encounterId];
    if (!next) return;
    const asDate = new Date(next);
    if (Number.isNaN(asDate.getTime())) {
      alert('Please pick a valid date and time.');
      return;
    }
    updateEncounter(encounterId, {
      reviewDate: asDate.toISOString(),
      reviewStatus: 'Rescheduled',
      reviewContactedAt: new Date().toISOString(),
      reviewContactedBy: currentUser?.id
    });
    setRescheduling(r => {
      const {
        [encounterId]: _,
        ...rest
      } = r;
      return rest;
    });
  };
  const handleCancelReschedule = (encounterId: string) => {
    setRescheduling(r => {
      const {
        [encounterId]: _,
        ...rest
      } = r;
      return rest;
    });
  };
  return <Card title="Review Due" subtitle={rows.length === 0 ? 'No reviews due within 2 days and none 2+ days overdue.' : `${rows.length} patient${rows.length === 1 ? '' : 's'} need follow-up.`} data-id="element-6045" data-mp="2i35jpcovyh4v">
      {/* Summary chips + search */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-6046" data-mp="3fpw868ueh7xw">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold" data-id="element-6047" data-mp="3fo1tvddmwe2s">
          <CalendarClock className="w-3.5 h-3.5" data-id="element-6048" data-mp="35ror7q042pvw" /> Review Due Soon:{' '}
          {counts.soon}
        </span>
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-semibold" data-id="element-6049" data-mp="1qaojb6exhtt8">
          <AlertTriangle className="w-3.5 h-3.5" data-id="element-6050" data-mp="1udkxmkxfr3rd" /> Review Overdue:{' '}
          {counts.overdue}
        </span>
        <div className="relative w-full sm:ml-auto sm:w-64" data-id="element-6051" data-mp="1mrfqb3d30gdv">
          <label htmlFor="review-due-search" className="sr-only" data-id="element-6052" data-mp="1huz105u5bvz7">
            Search reviews
          </label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-6053" data-mp="1wqf22ut58s53" />
          <input id="review-due-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, file no. or phone" className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-6054" data-mp="1rswqewnqryte" />
        </div>
      </div>
      <p className="mb-4 text-xs text-gray-500" data-id="element-6055" data-mp="18zsut5qyrbw5">
        <span className="font-semibold text-amber-700" data-id="element-6056" data-mp="lbhxgckh7ucm">Due Soon</span> — review
        date within 2 days of today.{' '}
        <span className="font-semibold text-red-700" data-id="element-6057" data-mp="gcf0zgikawg9">Overdue</span> — review
        date passed 2 or more days ago. Live · refreshed{' '}
        {now.toLocaleTimeString()}
      </p>

      {visibleRows.length === 0 ? <div className="text-center text-gray-500 py-10" data-id="element-6058" data-mp="3w0goftlyboer">
          <CalendarClock className="w-10 h-10 mx-auto mb-2 text-gray-300" data-id="element-6059" data-mp="4r044yuzufzm" />
          <p className="text-sm" data-id="element-6060" data-mp="8teppqq1rtul">
            {rows.length === 0 ? 'All caught up — no reviews due.' : `No reviews match "${search}".`}
          </p>
        </div> : <div className="overflow-x-auto" data-id="element-6061" data-mp="iravs444nylu">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-6062" data-mp="iq9li1uxrg56">
            <thead className="bg-gray-50" data-id="element-6063" data-mp="niar54xlm8m8">
              <tr data-id="element-6064" data-mp="njcdi5k2ehj9">
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-6065" data-mp="nkx1tdfsd48o">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-6066" data-mp="wka9c0kcniqr">
                  Full Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-6067" data-mp="11fzc0cro1fra">
                  Phone Number
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-6068" data-mp="2h08bbi7wv2gg">
                  Review Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-6069" data-mp="2d1j1u0pzu523">
                  File No.
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-6070" data-mp="34psyyef0enw2">
                  Follow-up
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-600" data-id="element-6071" data-mp="30rmw3hhaksgf">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-6072" data-mp="2qz7fanhqe9x4">
              {visibleRows.map(row => {
            const style = urgencyStyles[row.urgency];
            const isRescheduling = rescheduling[row.encounterId] !== undefined;
            return <Fragment key={row.encounterId} data-id="element-6073" data-mp="102dcdrtgtni0">
                    <tr className={`transition-colors ${style.row}`} data-id="element-6074" data-mp="v3a8iap81nbm">
                      <td className="px-4 py-2 align-top" data-id="element-6075" data-mp="v53gbtbkmdl7">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${style.pill}`} data-id="element-6076" data-mp="193ba5sdb8ys5">
                          {style.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 align-top font-medium text-gray-900" data-id="element-6077" data-mp="1e1w81l53b6df">
                        {row.patientName}
                      </td>
                      <td className="px-4 py-2 align-top text-gray-700" data-id="element-6078" data-mp="1sppqddlk8mzp">
                        {row.phone}
                      </td>
                      <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap" data-id="element-6079" data-mp="21r762uv22mnv">
                        {row.reviewDate.toLocaleString([], {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                      </td>
                      <td className="px-4 py-2 align-top font-mono text-sm text-blue-700" data-id="element-6080" data-mp="2lcz9uexgr216">
                        {row.bmcFileNumber}
                      </td>
                      <td className="px-4 py-2 align-top" data-id="element-6081" data-mp="27cy7ev1jvmjl">
                        {row.status === 'Contacted' ? <Badge variant="success" data-id="element-6082" data-mp="2bgnt9cqubq0x">
                            <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6083" data-mp="1xmd9zaxm4o29" />
                            Contacted
                          </Badge> : row.status === 'Rescheduled' ? <Badge variant="info" data-id="element-6084" data-mp="1nqjo30y07i7t">Rescheduled</Badge> : <Badge variant="warning" data-id="element-6085" data-mp="1sp3cd802l63c">Pending</Badge>}
                        {row.contactedAt && row.status === 'Contacted' && <div className="text-[10px] text-gray-500 mt-0.5 italic" data-id="element-6086" data-mp="1jo57knht4ujp">
                            {new Date(row.contactedAt).toLocaleString()}
                          </div>}
                      </td>
                      <td className="px-4 py-2 align-top text-right" data-id="element-6087" data-mp="3gkjcoq9c5y4h">
                        <div className="inline-flex flex-wrap gap-1 justify-end" data-id="element-6088" data-mp="3bm83mhqv7rnb">
                          {row.status !== 'Contacted' && <Button size="sm" variant="primary" onClick={() => handleMarkContacted(row.encounterId)} title="Mark as contacted" data-id="element-6089" data-mp="37jbdm5yzwt9c">
                              <PhoneCall className="w-3.5 h-3.5 mr-1" data-id="element-6090" data-mp="3qcp5213i2hk7" />
                              Mark Contacted
                            </Button>}
                          <Button size="sm" variant="outline" onClick={() => handleOpenReschedule(row)} title="Reschedule review" data-id="element-6091" data-mp="2ex0hb7if9eic">
                            <CalendarPlus className="w-3.5 h-3.5 mr-1" data-id="element-6092" data-mp="zbexlwklp02t" />
                            Reschedule
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => handleCancelReview(row.encounterId, row.patientName)} title="Cancel review" data-id="element-6093" data-mp="186cwdkafj722">
                            <XCircle className="w-3.5 h-3.5 mr-1" data-id="element-6094" data-mp="1r2uqg8cr3j1h" />
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isRescheduling && <tr className="bg-blue-50 border-b border-blue-200" data-id="element-6095" data-mp="2xl3yokrjz53n">
                        <td colSpan={7} className="px-4 py-3" data-id="element-6096" data-mp="2tl3e0idv1xo5">
                          <div className="flex flex-wrap items-end gap-3" data-id="element-6097" data-mp="2tmwknxoh4hw5">
                            <div data-id="element-6098" data-mp="37jtb3y0ojxci">
                              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-6099" data-mp="32l8juo3eoo07">
                                New review date & time
                              </label>
                              <input type="datetime-local" value={rescheduling[row.encounterId]} onChange={e => setRescheduling(r => ({
                        ...r,
                        [row.encounterId]: e.target.value
                      }))} className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" data-id="element-6100" data-mp="2etb6uto7zpxi" />
                            </div>
                            <Button size="sm" variant="primary" onClick={() => handleConfirmReschedule(row.encounterId)} data-id="element-6101" data-mp="bgbz3oza622t">
                              Confirm new date
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleCancelReschedule(row.encounterId)} data-id="element-6102" data-mp="ucbtt7jn7jq3">
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
          })}
            </tbody>
          </table>
        </div>}
    </Card>;
};